import type { GraphPath } from '@ipfabric/utils';
import { onlyValue } from '@ipfabric/utils/fp/onlyValue';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { prop, propOf } from '@ipfabric/utils/fp/prop';
import type { RequiredBy } from '@ipfabric/utils/types/RequiredBy';
import { ContextMenu } from 'components/DropdownMenu/ContextMenu';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { GraphState } from 'components/Graph/GraphState';
import { collapseNotSelectedNodes } from 'components/Graph/commands/collapsing/collapseNotSelectedNodes';
import { collapseSelectedNodes } from 'components/Graph/commands/collapsing/collapseSelectedNodes';
import { uncollapseNode } from 'components/Graph/commands/collapsing/uncollapseNode';
import { uncollapseSelectedNodes } from 'components/Graph/commands/collapsing/uncollapseSelectedNodes';
import { hideNotSelectedNodes } from 'components/Graph/commands/hideNotSelectedNodes';
import { hideSelectedNodes } from 'components/Graph/commands/hideSelectedNodes';
import { closeNodeContextMenu } from 'components/Graph/commands/nodes/closeNodeContextMenu';
import { selectNeighbors } from 'components/Graph/commands/nodes/selectNeighbors';
import {
  type ShowGraphLayoutDialogCommand,
  showGraphLayoutDialog,
} from 'components/Graph/commands/showGraphLayoutDialog';
import { unhideSelectedNodes } from 'components/Graph/commands/unhideSelectedNodes';
import { isCollapsedNodeId } from 'components/Graph/model/GraphData';
import {
  type NodeStableDataRecord,
  selectNodesStableData,
} from 'components/Graph/model/selectors/selectNodesStableData';
import { selectNotSelectedHiddenNodeIds } from 'components/Graph/model/selectors/selectNotSelectedHiddenNodeIds';
import { selectNotSelectedVisibleNodeIds } from 'components/Graph/model/selectors/selectNotSelectedVisibleNodeIds';
import { selectSelectedHiddenNodeIds } from 'components/Graph/model/selectors/selectSelectedHiddenNodeIds';
import { selectSelectedVisibleNodeIds } from 'components/Graph/model/selectors/selectSelectedVisibleNodeIds';
import { selectSomeCollapsedNodeGroupSelected } from 'components/Graph/model/selectors/selectSomeCollapsedNodeGroupSelected';
import { useLocale } from 'containers/App/LocaleContext';
import { array, option } from 'fp-ts';
import { constNull, pipe } from 'fp-ts/lib/function';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { hideNode } from './commands/hideNode';
import { hasNodeDetail, showNodeDetail } from './commands/showNodeDetail';
import { unhideNode } from './commands/unhideNode';
import { selectPathLookupApplied } from './model/selectors/graphMetadata/selectPathLookupApplied';

const selectShowNodesLayoutDialogCommand: (
  s: GraphState,
) => null | RequiredBy<ShowGraphLayoutDialogCommand, 'nodeSns'> =
  createScopedSelector(() =>
    createSelector(
      prop('graph', 'selectedNodeIds'),
      selectNodesStableData,
      (selectedNodeIds, nodes) => {
        if (selectedNodeIds.size < 2) {
          // nothing to layout with less than two nodes
          return null;
        }
        const selectedNodes = pipe(
          selectedNodeIds,
          plainSet.toArray,
          array.map(propOf<NodeStableDataRecord>(nodes)),
        );
        return pipe(
          selectedNodes,
          array.map(prop('parentPath')),
          onlyValue(),
          option.filter(
            (path: GraphPath | null): path is GraphPath => path !== null,
          ),
          option.fold(constNull, (path) => ({
            path,
            nodeSns: selectedNodes.map(prop('sn')),
          })),
        );
      },
    ),
  );

interface Props {
  container: HTMLElement | null;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable, big decision logic
export const GraphNodeContextMenu: FC<Props> = (props) => {
  const msgMenu = useLocale().canvasGraph.graphNodeContextMenu;

  const ctxProps = useGraphContextSelectors({
    collapsedNodeGroups: prop('graph', 'collapsedNodeGroups'),
    dispatch: prop('dispatch'),
    hiddenNodes: prop('graph', 'hiddenNodes'),
    nodeContextMenu: prop('nodeContextMenu'),
    nodes: prop('graph', 'nodes'),
    nodesEdgesMap: prop('graph', 'nodesEdgesMap'),
    notSelectedHiddenNodeIds: selectNotSelectedHiddenNodeIds,
    notSelectedVisibleNodeIds: selectNotSelectedVisibleNodeIds,
    pathLookupApplied: selectPathLookupApplied,
    peekingAtHiddenNodes: prop('peekingAtHiddenNodes'),
    selectedHiddenNodeIds: selectSelectedHiddenNodeIds,
    selectedNodeIds: prop('graph', 'selectedNodeIds'),
    selectedVisibleNodeIds: selectSelectedVisibleNodeIds,
    showNodesLayoutCommand: selectShowNodesLayoutDialogCommand,
    someCollapsedNodeGroupSelected: selectSomeCollapsedNodeGroupSelected,
  });

  if (
    ctxProps.nodeContextMenu === null ||
    ctxProps.nodesEdgesMap[ctxProps.nodeContextMenu.nodeId] === undefined
  ) {
    return null;
  }

  const { nodeId } = ctxProps.nodeContextMenu;
  const node = ctxProps.nodes[nodeId];

  const orphanedNode = ctxProps.nodesEdgesMap[nodeId]?.size === 0;
  const isCurrentOnlySelected =
    ctxProps.selectedNodeIds.size === 0 ||
    (ctxProps.selectedNodeIds.size === 1 &&
      ctxProps.selectedNodeIds.has(nodeId));

  const makeSelectNeighbors = (distance: number) => () => {
    ctxProps.dispatch(selectNeighbors, { nodeId, distance });
  };

  return (
    <ContextMenu
      container={props.container}
      onClose={() => {
        ctxProps.dispatch(closeNodeContextMenu);
      }}
      position={ctxProps.nodeContextMenu.position}
      items={[
        isCurrentOnlySelected && {
          // Fixed noUncheckedIndexedAccess NIM-19408
          disabled: !hasNodeDetail(node!.type),
          label: msgMenu.showDetail,
          onClick: () => {
            ctxProps.dispatch(showNodeDetail, { nodeId });
          },
        },
        {
          disabled: orphanedNode,
          label: msgMenu.selectNeighbors,
          children: [1, 2, 3].map((number) => ({
            label: (
              <FormattedMessage
                id='canvasGraph.graphNodeContextMenu.selectNeighborsLevel'
                defaultMessage={msgMenu.selectNeighborsLevel}
                values={{ number }}
              />
            ),
            onClick: makeSelectNeighbors(number),
          })),
        },
        ctxProps.selectedNodeIds.size === 0 &&
          !ctxProps.pathLookupApplied &&
          !ctxProps.hiddenNodes.has(ctxProps.nodeContextMenu.nodeId) && {
            label: msgMenu.hideNode,
            onClick: () => {
              ctxProps.dispatch(hideNode, { nodeId });
            },
          },
        ctxProps.selectedNodeIds.size === 0 &&
          !ctxProps.pathLookupApplied &&
          ctxProps.hiddenNodes.has(ctxProps.nodeContextMenu.nodeId) && {
            label: msgMenu.unhideNode,
            onClick: () => {
              ctxProps.dispatch(unhideNode, { nodeId });
            },
          },
        ctxProps.selectedVisibleNodeIds.size !== 0 &&
          !ctxProps.pathLookupApplied && {
            label: msgMenu.hideSelected,
            onClick: () => {
              ctxProps.dispatch(hideSelectedNodes);
            },
          },
        ctxProps.selectedVisibleNodeIds.size !== 0 &&
          !ctxProps.pathLookupApplied && {
            label: msgMenu.hideNotSelected,
            onClick: () => {
              ctxProps.dispatch(hideNotSelectedNodes);
            },
          },
        ctxProps.peekingAtHiddenNodes &&
          ctxProps.selectedHiddenNodeIds.size !== 0 &&
          !ctxProps.pathLookupApplied && {
            label: msgMenu.unhideSelected,
            onClick: () => {
              ctxProps.dispatch(unhideSelectedNodes);
            },
          },
        ctxProps.selectedVisibleNodeIds.size > 0 &&
          !ctxProps.pathLookupApplied && {
            disabled:
              ctxProps.selectedVisibleNodeIds.size <= 1 ||
              ctxProps.selectedHiddenNodeIds.size > 0,
            label: msgMenu.collapseSelected,
            onClick: () => {
              ctxProps.dispatch(collapseSelectedNodes);
            },
          },
        ctxProps.selectedNodeIds.size > 0 &&
          !ctxProps.pathLookupApplied && {
            disabled:
              ctxProps.notSelectedVisibleNodeIds.size <= 1 ||
              ctxProps.selectedHiddenNodeIds.size > 0 ||
              (ctxProps.peekingAtHiddenNodes &&
                ctxProps.notSelectedHiddenNodeIds.size > 0),
            label: msgMenu.collapseNotSelected,
            onClick: () => {
              ctxProps.dispatch(collapseNotSelectedNodes);
            },
          },
        ctxProps.someCollapsedNodeGroupSelected &&
          !ctxProps.pathLookupApplied && {
            label: msgMenu.uncollapseSelected,
            onClick: () => {
              ctxProps.dispatch(uncollapseSelectedNodes);
            },
          },
        isCollapsedNodeId(ctxProps.collapsedNodeGroups)(nodeId) &&
          !ctxProps.someCollapsedNodeGroupSelected &&
          !ctxProps.pathLookupApplied && {
            label: msgMenu.uncollapseSingle,
            onClick: () => {
              ctxProps.dispatch(uncollapseNode, { nodeId });
            },
          },
        ctxProps.selectedNodeIds.size > 1 &&
          !ctxProps.pathLookupApplied && {
            disabled: ctxProps.showNodesLayoutCommand === null,
            label: msgMenu.layoutAs,
            onClick: () => {
              ctxProps.dispatch(
                showGraphLayoutDialog,
                ctxProps.showNodesLayoutCommand!,
              );
            },
          },
      ]}
    />
  );
};
